import type { SubjectCanvasNode, SubjectCollection } from '@/types'

type LegacySubjectCollection = Omit<SubjectCollection, 'memberOrder'> & {
  memberOrder?: unknown
  sceneId?: unknown
  binId?: unknown
}

function getMemberSubjectId(member: unknown): string | null {
  if (typeof member === 'string') return member
  if (member && typeof member === 'object' && typeof (member as { subjectId?: unknown }).subjectId === 'string') {
    return (member as { subjectId: string }).subjectId
  }
  return null
}

export function normalizeSubjectCollectionMemberOrder(memberOrder: unknown): SubjectCollection['memberOrder'] {
  if (!Array.isArray(memberOrder)) return []
  return memberOrder.flatMap(member => {
    const subjectId = getMemberSubjectId(member)
    if (!subjectId) return []
    if (member && typeof member === 'object') return [member as SubjectCollection['memberOrder'][number]]
    return [{ subjectId }]
  })
}

export function subjectCollectionMemberIds(collection: Pick<SubjectCollection, 'memberOrder'>): string[] {
  return normalizeSubjectCollectionMemberOrder(collection.memberOrder).map(member => member.subjectId)
}

export function getSubjectCollectionHomeSceneId(
  collection: Pick<SubjectCollection, 'homeSceneId'> & { sceneId?: unknown },
): string | undefined {
  return collection.homeSceneId ?? (typeof collection.sceneId === 'string' ? collection.sceneId : undefined)
}

export function normalizeSubjectCollectionRecord(collection: SubjectCollection): {
  collection: SubjectCollection
  legacyBinId?: string
  changed: boolean
} {
  const legacy = collection as LegacySubjectCollection
  const memberOrder = normalizeSubjectCollectionMemberOrder(legacy.memberOrder)
  const homeSceneId = getSubjectCollectionHomeSceneId(legacy)
  const binId = legacy.binId
  const rest = { ...(legacy as Record<string, unknown>) }
  delete rest.sceneId
  delete rest.binId
  delete rest.memberOrder
  const normalized = {
    ...rest,
    homeSceneId,
    memberOrder,
  } as SubjectCollection
  const legacyBinId = typeof binId === 'string' ? binId : undefined
  const rawMemberOrder = Array.isArray(legacy.memberOrder) ? legacy.memberOrder : []
  const changed =
    legacy.sceneId !== undefined ||
    legacy.binId !== undefined ||
    legacy.homeSceneId !== homeSceneId ||
    rawMemberOrder.length !== memberOrder.length ||
    rawMemberOrder.some((member, index) => member !== memberOrder[index])

  return { collection: normalized, legacyBinId, changed }
}

export interface SubjectCollectionMembershipReconciliation {
  collectionsToSet: Array<[string, SubjectCollection]>
  collectionIdsToDelete: string[]
  subjectsToSet: Array<[string, SubjectCanvasNode]>
}

export function reconcileSubjectCollectionMemberships(
  collections: ReadonlyMap<string, SubjectCollection>,
  subjects: ReadonlyMap<string, SubjectCanvasNode>,
): SubjectCollectionMembershipReconciliation {
  const subjectsByCollection = new Map<string, string[]>()
  const subjectsToSetById = new Map<string, SubjectCanvasNode>()

  const queueSubjectUpdate = (
    subjectId: string,
    subject: SubjectCanvasNode,
    updates: Partial<SubjectCanvasNode>,
  ): void => {
    const base = subjectsToSetById.get(subjectId) ?? subject
    subjectsToSetById.set(subjectId, { ...base, ...updates })
  }

  for (const [subjectId, subject] of subjects) {
    const collectionId = subject.collectionId
    if (!collectionId) continue

    const collection = collections.get(collectionId)
    if (!collection) {
      queueSubjectUpdate(subjectId, subject, { collectionId: undefined })
      continue
    }

    if (subject.stackId || subject.binId) {
      queueSubjectUpdate(subjectId, subject, { stackId: undefined, binId: undefined })
    }

    const bucket = subjectsByCollection.get(collectionId)
    if (bucket) bucket.push(subjectId)
    else subjectsByCollection.set(collectionId, [subjectId])
  }

  const collectionsToSet: Array<[string, SubjectCollection]> = []
  const collectionIdsToDelete: string[] = []

  for (const [collectionId, collection] of collections) {
    const normalized = normalizeSubjectCollectionRecord(collection)
    const normalizedCollection = normalized.collection
    const backrefIds = subjectsByCollection.get(collectionId) ?? []
    const seen = new Set<string>()
    const nextMemberOrder: SubjectCollection['memberOrder'] = []

    for (const member of normalizedCollection.memberOrder) {
      const subjectId = member.subjectId
      if (seen.has(subjectId)) continue

      const subject = subjects.get(subjectId)
      if (!subject) {
        seen.add(subjectId)
        nextMemberOrder.push(member)
        continue
      }
      if (subject.collectionId && subject.collectionId !== collectionId) continue
      if (!subject.collectionId || subject.stackId || subject.binId) {
        queueSubjectUpdate(subjectId, subject, {
          collectionId,
          stackId: undefined,
          binId: undefined,
        })
      }

      seen.add(subjectId)
      nextMemberOrder.push(member)
    }

    const appended = backrefIds
      .filter(subjectId => !seen.has(subjectId))
      .sort((a, b) => {
        const subjectA = subjects.get(a)
        const subjectB = subjects.get(b)
        return (
          (subjectA?.createdAt ?? '').localeCompare(subjectB?.createdAt ?? '') ||
          (subjectA?.name ?? '').localeCompare(subjectB?.name ?? '') ||
          a.localeCompare(b)
        )
      })
    nextMemberOrder.push(...appended.map(subjectId => ({ subjectId })))

    // A collection whose members have all been removed or repointed elsewhere
    // (e.g. after the memberOrder whole-array race or a concurrent move) is
    // dissolved rather than left as an empty ghost card. Members whose subject
    // record is simply not loaded yet are preserved above (they are pushed back
    // into nextMemberOrder), so an empty result here means genuine emptiness.
    if (nextMemberOrder.length === 0) {
      collectionIdsToDelete.push(collectionId)
    } else if (!sameMemberArray(normalizedCollection.memberOrder, nextMemberOrder) || normalized.changed) {
      collectionsToSet.push([collectionId, { ...normalizedCollection, memberOrder: nextMemberOrder }])
    }
  }

  return {
    collectionsToSet,
    collectionIdsToDelete,
    subjectsToSet: Array.from(subjectsToSetById.entries()),
  }
}

function sameMemberArray(a: SubjectCollection['memberOrder'], b: SubjectCollection['memberOrder']): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i].subjectId !== b[i].subjectId) return false
  }
  return true
}
